/** The company, its sites, its meters and its filing windows — **B-23's buildable half**.
 *
 * Until this existed, none of it could be created outside `seed.py`, which refuses to run in
 * production. A customer could sign in, read a workspace somebody else had seeded, and never put
 * their own company in it.
 *
 * **Four rules the screen has to get right, and three of them are about refusals.**
 *
 * - **An Admin's screen, and it says so to everybody else.** Changing the shape of the company
 *   changes what every figure is about, which filings exist and what the census expects. A
 *   Contributor is told who does it rather than shown a form that 403s.
 * - **The server's sentence is what is shown.** Every refusal on this surface is written to be
 *   read — *"Sierravale Foods Ltd is already the parent of this group"*, *"FY2025 already covers
 *   part of that window"*. Replacing them with "Could not save" would throw away the only part
 *   that says what to do.
 * - **The consolidation approach is asked, never defaulted.** The Corporate Standard makes it a
 *   `shall` (verification log S-01), and it decides whether a joint venture is in the inventory at
 *   all. A pre-selected dropdown is a choice made on the customer's behalf that every figure in
 *   the period inherits — so nothing is selected until somebody selects it.
 * - **Retire, never delete.** There is no delete control because there is no delete route: a
 *   removed site takes the meaning of every figure that cites it. Retiring says what it will do to
 *   the site's meters before it is pressed.
 *
 * **Creating the workspace itself is not here**, and that is ADR-0043 rather than an omission: the
 * tenant and its first Admin need somebody authorised before any membership exists. The panel at
 * the foot of the screen says so, because an administrator who finds no heading concludes there is
 * nothing to find.
 */

import { useState } from "react";
import { Link } from "@tanstack/react-router";

import { ReadFailed, WriteFailed } from "../components/Loaded";
import { PageHeader } from "../components/PageHeader";
import { OrganisationLinks } from "./settingsLinks";
import { Empty, Panel } from "../components/Panel";
import { useMarketDefaults } from "../api/config";
import { regimeLabel, useRegimes } from "../api/regimes";
import { useMe } from "../api/auth";
import { isPeriodClosed } from "../api/refusal";
import { useEntities } from "../api/queries";
import { useFacilities } from "../api/evidence";
import { useAnswerLease } from "../api/org";
import type { Facility, Meter, MeterIn, ReportingPeriodIn } from "../api/provisioning";
import {
  useCreateEntity,
  useCreateFacility,
  useCreateMeter,
  useCreatePeriod,
  useMeters,
  useRetireFacility,
  useRetireMeter,
} from "../api/provisioning";
import { shortDate, unitLabel } from "../lib/format";
import { RequireSession } from "../shell/RequireSession";
import { RecordPanel, RecordTabs } from "./RecordTabs";
import { ScreenFrame } from "./ScreenFrame";
import { AddPanel, BasisAndLimits } from "./ScreenParts";
import parts from "./ScreenParts.module.css";
import own from "./Organisation.module.css";
import { useUrlTab } from "./useUrlTab";
import { keepSearch } from "./search";
import {
  JURISDICTIONS,
  countryOf,
  LEASE_LABEL,
  MONTHS,
  SITE_TYPES,
  type FacilityTypeValue,
  type JurisdictionValue,
} from "./organisationOptions";
import { useScreen } from "./useScreen";
import { EntityCorrection, SiteCorrection } from "./OrganisationEdit";
import { GridRegionField } from "./GridRegionField";
import { GRID_UNKNOWN } from "./gridRegionChoice";
import { GroupSection } from "./OrganisationGroup";
import { OrganisationSummary } from "./OrganisationSummary";
import { LeaseCorrections } from "./LeaseCorrections";
import { PostCloseRetry } from "./PeriodLock";

function cls(...names: Array<string | false | undefined>) {
  return names.filter(Boolean).join(" ");
}

/** The three views. Companies and sites are one tab because they are one question - *what is this
 * workspace made of* - and the browser gate reads both lists at once. Group and Filing windows are
 * the two things that hang off it. The first is the default, so the URL carries no `tab` for it. */
const ORG_TABS = [
  { key: "structure", label: "Companies and sites" },
  { key: "group", label: "Group" },
  { key: "windows", label: "Reporting periods" },
] as const;

type MeterKindValue = MeterIn["kind"];
type ApproachValue = ReportingPeriodIn["boundary_approach"];

const LEASE_HELP: Record<string, string> = {
  owned: "The company owns it. In scope under every consolidation approach.",
  finance_lease:
    "On the balance sheet as a wholly owned asset. In scope under every approach — ask your accountant if you are not sure: this is an accounting classification, not a lease length.",
  operating_lease:
    "Not on the balance sheet. In scope only under operational control, and only if you operate it.",
  occupied_under_licence:
    "A serviced office or a shared site. EmitIQ treats this as an operating lease — our reading, not the standard's words.",
  unknown:
    "Leave this until you have asked. An unanswered site is held out of a filing's stated total with a finding naming it, which is safer than a guess that silently lands in one.",
};

/** The GHG Protocol's consolidation approaches (verification log S-01).
 *
 * Each carries what it *does*, because "equity share" tells a reader nothing and the choice
 * decides whether a joint venture's emissions are in the inventory at all.
 */
const APPROACHES: ReadonlyArray<{ value: ApproachValue; label: string; means: string }> = [
  {
    value: "equity_share",
    label: "Equity share",
    means: "account for emissions in proportion to the share of equity held in each operation. EmitIQ records no ownership share, so a filing on this approach cannot add a subsidiary's sites",
  },
  {
    value: "financial_control",
    label: "Financial control",
    means: "account for 100% of operations the company can direct for economic benefit",
  },
  {
    value: "operational_control",
    label: "Operational control",
    means: "account for 100% of operations where the company sets the operating policies",
  },
];

const METER_KINDS: readonly MeterKindValue[] = [
  "electricity",
  "natural_gas",
  "steam",
  "heat",
  "cooling",
  "fuel_card",
  "refrigerant_log",
  "other",
];

export function OrganisationScreen() {
  const screen = useScreen();
  const { data: me } = useMe();
  const entities = useEntities();
  const facilities = useFacilities();
  const [tab, selectTab] = useUrlTab(ORG_TABS);

  const [openSite, setOpenSite] = useState<string | null>(null);
  /** Which company's and which site's correction form is open - one of each at a time, and the id
   * is the state, for the reason `openSite` and `retiring` are: a shared flag would put one form
   * under whichever row was clicked last. */
  const [correctingEntity, setCorrectingEntity] = useState<string | null>(null);
  const [correctingSite, setCorrectingSite] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  /** The refusal, not a sentence from it. Four forms share this slot, and three of them can be
   * refused for reasons with different remedies — a 403 to a role that is no longer Admin, a 409
   * naming the group's existing parent, and a request whose outcome nobody can tell. They read
   * identically before this. */
  const [refusal, setRefusal] = useState<unknown>(null);

  const isAdmin = me?.role === "admin";

  const [retrying, setRetrying] = useState(false);
  /** The refused write that can be sent again with a reason, if there is one (ADR-0094). */
  const [again, setAgain] = useState<{
    run: (postCloseReason: string) => Promise<unknown>;
    said: string;
  } | null>(null);

  /** **A write's result belongs to the tab it was made on** (journey walk, 2026-10-31). "Acme added."
   * stayed on screen after the reader moved to Filing windows, where it described nothing in front of
   * them. Reset while rendering (React's own pattern for state that depends on a changed value) rather
   * than in a click handler, so the browser's Back button clears it too, and rather than in an effect,
   * which would paint the stale sentence once first. */
  const [shownOn, setShownOn] = useState(tab);
  if (shownOn !== tab) {
    setShownOn(tab);
    setMessage(null);
    setRefusal(null);
    setAgain(null);
  }

  const report: Report = (result, said, retry) => {
    setMessage(null);
    setRefusal(null);
    setAgain(null);
    return result.then(
      () => setMessage(said),
      (error: unknown) => {
        setRefusal(error);
        if (retry) setAgain({ run: retry, said });
      },
    );
  };

  if (!isAdmin) {
    return (
      <RequireSession>
        <ScreenFrame screen={screen} title="Organisation" crumb="companies, sites and reporting periods">
          <Panel title="Organisation" column="1 / 9">
            {/* Told who does it, rather than shown a form that predictably 403s. Including the
                per-meter retirement: that route is `MAY_ADMINISTER` server-side, so a control
                offered here would 403 for every role this branch is rendered to. `data-testid`
                rather than the sentence alone, so a test can assert the pair structurally —
                `Tasks.tsx`'s precedent, for `formOrWhoCan`'s reason. */}
            <p className={cls(own.note)} data-testid="org-who-can">
              An <strong>Admin</strong> sets up companies, sites and reporting periods, corrects them
              afterwards, records who owns whom and since when, states a site&rsquo;s lease
              treatment, and retires a site or a supply point that has come out.
              Changing the shape of the company changes what every figure is about, which filings
              exist, what a filed total contains and what the expected bills expects, so it is not a
              shared setting.
            </p>
          </Panel>
        </ScreenFrame>
      </RequireSession>
    );
  }

  return (
    <RequireSession>
      <ScreenFrame
        screen={screen}
        title="Organisation"
        crumb="companies, sites and reporting periods"
        bodyScrolls
      >
        <PageHeader
          subtitle="What this workspace is made of, who owns whom, and the periods each company files for."
          actions={<OrganisationLinks current="/organisation" search={screen} />}
          tabs={
            <RecordTabs
              tabs={ORG_TABS}
              tab={tab}
              onSelect={selectTab}
              label="Organisation views"
              saying={`Showing ${ORG_TABS.find((entry) => entry.key === tab)?.label ?? "Companies and sites"}`}
            />
          }
        />
        <OrganisationSummary
          entities={entities.isError ? { status: "failed" } : entities.isPending ? { status: "pending" } : { status: "ready", rows: entities.data ?? [] }}
          sites={facilities.isError ? { status: "failed" } : facilities.isPending ? { status: "pending" } : { status: "ready", rows: facilities.data ?? [] }}
        />
        {message ? (
          <p className={cls(own.saved)} role="status" data-testid="org-message">
            {message}
          </p>
        ) : null}
        {/* `org-error` is kept as the id, because the gate selects it and asserts the server's
            own sentence and that it renders wide enough to read. The branch is a separate
            attribute. */}
        {/* A retirement back-dated into a closed period (ADR-0094). An Admin may send it again with
            a reason; the period stays closed. Every other write here has no `again` and is told
            who can. */}
        <PostCloseRetry
          error={refusal}
          pending={retrying}
          onRecord={
            again
              ? (reason) => {
                  setRetrying(true);
                  void report(again.run(reason), again.said, again.run).finally(() =>
                    setRetrying(false),
                  );
                }
              : undefined
          }
        />
        <WriteFailed
          error={isPeriodClosed(refusal) ? null : refusal}
          of="this workspace's structure"
          testId="org-error"
          className={cls(own.failed)}
          onReload={() => {
            void entities.refetch();
            void facilities.refetch();
            setRefusal(null);
          }}
        />

        <div className={parts.area}>
          <RecordPanel tab={tab}>
            <div className={parts.stack}>
        {tab === "structure" ? (
          <>
        <Panel
          title="Companies"
          sub="Each one files. A group has one parent, and that is who files for it"
          column="1 / 13"
        >
          {/* **A failed read is not "no companies yet"** (EXP-29). That sentence instructs the
              reader to add the company that files; on a dropped request it instructs them to
              create a duplicate of one that already exists. */}
          {entities.error ? (
            <ReadFailed
              of="The companies in this workspace"
              error={entities.error}
              onRetry={() => void entities.refetch()}
            />
          ) : entities.isPending ? (
            <Empty>Loading…</Empty>
          ) : entities.data?.length ? (
            <ul className={cls(own.list)} data-testid="entities">
              {entities.data.map((entity) => (
                <li key={entity.id} className={cls(own.row)}>
                  <span className={cls(own.name)}>{entity.legal_name}</span>
                  <span className={cls(own.meta)}>
                    {entity.jurisdiction}
                    {" · "}
                    {/* **Zero said out loud.** An entity with no sites contributes no figures, and
                        a blank here invites somebody to pick it and conclude the inventory is
                        empty. */}
                    {entity.facility_count === 0
                      ? "no sites yet, so it contributes no figures"
                      : `${entity.facility_count} site${entity.facility_count === 1 ? "" : "s"}`}
                  </span>
                  <button
                    type="button"
                    className={cls(own.inlineButton)}
                    data-testid={`correct-entity-${entity.id}`}
                    aria-expanded={correctingEntity === entity.id}
                    onClick={() =>
                      setCorrectingEntity(correctingEntity === entity.id ? null : entity.id)
                    }
                  >
                    {correctingEntity === entity.id ? "Hide" : "Correct"}
                  </button>
                  {correctingEntity === entity.id ? (
                    <EntityCorrection
                      entityId={entity.id}
                      onSubmit={report}
                      onDone={() => setCorrectingEntity(null)}
                    />
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <Empty>No companies yet. Add the one that files.</Empty>
          )}
          <AddPanel label="Add a company" testId="entity-panel">
            <EntityForm onSubmit={report} />
          </AddPanel>
        </Panel>

        <Panel
          title="Sites"
          sub="Where the energy is used. A site's country decides its emission factors"
          column="1 / 13"
          action={
            <Link
              to="/organisation/structure"
              search={keepSearch}
              className={cls(own.crossLink)}
              data-testid="register-structure-link"
            >
              Register many from a file
            </Link>
          }
        >
          {facilities.error ? (
            <ReadFailed
              of="The sites in this workspace"
              error={facilities.error}
              onRetry={() => void facilities.refetch()}
            />
          ) : facilities.isPending ? (
            <Empty>Loading…</Empty>
          ) : facilities.data?.length ? (
            <ul className={cls(own.list)} data-testid="facilities">
              {facilities.data.map((site) => (
                <li key={site.id} className={cls(own.row)}>
                  <span className={cls(own.name)}>
                    {/* The site's own page (Batch 5): its figures, sources, meters and history. */}
                    <Link
                      to="/organisation/sites/$facilityId"
                      params={{ facilityId: site.id }}
                      search={keepSearch}
                      className={cls(own.crossLink)}
                      data-testid={`site-page-${site.id}`}
                    >
                      {site.name}
                    </Link>
                  </span>
                  <span className={cls(own.meta)}>
                    {site.legal_entity_name} · {site.jurisdiction}
                    {site.grid_region ? ` · ${site.grid_region}` : ""}
                    {" · "}
                    {/* **ADR-0034.** On the list rather than behind an edit, because the thing a
                        reader needs is *which sites have not been answered* — an unanswered one
                        holds every filing it contributes to. */}
                    <span className={site.lease_type === "unknown" ? cls(own.unanswered) : undefined}>
                      {LEASE_LABEL[site.lease_type] ?? site.lease_type}
                    </span>
                  </span>
                  <button
                    type="button"
                    className={cls(own.inlineButton)}
                    aria-expanded={openSite === site.id}
                    onClick={() => setOpenSite(openSite === site.id ? null : site.id)}
                  >
                    {openSite === site.id ? "Hide" : "Meters and retirement"}
                  </button>
                  <button
                    type="button"
                    className={cls(own.inlineButton)}
                    data-testid={`correct-site-${site.id}`}
                    aria-expanded={correctingSite === site.id}
                    onClick={() => setCorrectingSite(correctingSite === site.id ? null : site.id)}
                  >
                    {correctingSite === site.id ? "Hide" : "Correct details"}
                  </button>
                  {correctingSite === site.id ? (
                    <SiteCorrection
                      siteId={site.id}
                      entities={(entities.data ?? []).map((e) => ({ id: e.id, name: e.legal_name }))}
                      onSubmit={report}
                      onDone={() => setCorrectingSite(null)}
                    />
                  ) : null}
                  <LeaseTreatment site={site} onSubmit={report} />
                  {openSite === site.id ? (
                    <SitePanel siteId={site.id} siteName={site.name} onSubmit={report} />
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <Empty>No sites yet. Add one to the company that operates it.</Empty>
          )}
          {/* **Not rendered against a failed read.** With no entities the form says *"add a
              company first"*, which is an instruction about this workspace's setup — and the panel
              above already says the read failed, so a second sentence contradicting it is worse
              than none. */}
          {entities.error ? null : (
            <AddPanel label="Add a site" testId="facility-panel">
              <FacilityForm
                entities={(entities.data ?? []).map((e) => ({ id: e.id, name: e.legal_name }))}
                onSubmit={report}
              />
            </AddPanel>
          )}
        </Panel>

          </>
        ) : null}

        {tab === "group" ? (
        <Panel
          title="Group"
          sub="Who owns whom, and since when. A company bought in August is not the same as one owned for ever"
          column="1 / 13"
        >
          {entities.error ? (
            <ReadFailed
              of="The companies a group is made of"
              error={entities.error}
              onRetry={() => void entities.refetch()}
            />
          ) : (
            <GroupSection
              entities={(entities.data ?? []).map((e) => ({ id: e.id, name: e.legal_name }))}
              onSubmit={report}
            />
          )}
        </Panel>

        ) : null}

        {tab === "windows" ? (
        <Panel
          title="Reporting periods"
          sub="A period belongs to one company and one regime — the same year is filed twice when two regimes apply"
          column="1 / 13"
        >
          {entities.error ? (
            <ReadFailed
              of="The companies a reporting period could belong to"
              error={entities.error}
              onRetry={() => void entities.refetch()}
            />
          ) : (
            <>
              {/* **Lists first.** The windows already declared are read from the workspace itself
                  (the same list the period selector offers), so a reader sees what exists before
                  being asked to add another - and a second window for the same company and regime
                  is the refusal the form would otherwise have to explain. */}
              <PeriodList periods={screen.organization?.periods ?? []} />
              <AddPanel label="Add a reporting period" testId="period-panel">
                <PeriodForm
                  entities={(entities.data ?? []).map((e) => ({ id: e.id, name: e.legal_name }))}
                  onSubmit={report}
                />
              </AddPanel>
            </>
          )}
        </Panel>
        ) : null}

        {/* **The absence, stated.** An administrator who finds no heading concludes there is
            nothing to find — the same rule the Administration screen follows for its seven
            unbuilt sections. Ranked, not deleted: one click away on every tab. */}
        <BasisAndLimits testId="org-basis">
          <p data-testid="org-creating-workspace" data-references="ADR-0043 ADR-0046">
            A new <strong>workspace</strong> and its first Admin are created by EmitIQ, not from
            inside a workspace: that one act has no membership to be scoped by, so it is a
            separate step for EmitIQ, and the first Admin sets their own password from an
            invitation. Ask your EmitIQ contact if you need another.
          </p>
          <p>
            Inviting a colleague is done on <strong>People &amp; roles</strong>: it produces a
            single-use link for you to hand over.
          </p>
        </BasisAndLimits>
            </div>
          </RecordPanel>
        </div>
      </ScreenFrame>
    </RequireSession>
  );
}

/** The filing windows this workspace has declared, one row each: whose, which regime, which dates.
 *
 * A read-only list from the workspace record the screen already holds - no new request, and nothing
 * to correct here (a window's dates are what a filing is about, and changing one is its own act).
 * Empty says so and says what to do, rather than printing nothing above a form. */
export function PeriodList({
  periods,
}: {
  periods: ReadonlyArray<{
    id: string;
    label: string;
    legal_entity_name: string;
    regime_key: string;
    start_date: string;
    end_date: string;
    status: string;
  }>;
}) {
  const regimes = useRegimes();
  if (periods.length === 0) {
    return <Empty>No reporting periods yet. Add the first one for the company that files.</Empty>;
  }
  return (
    <ul className={cls(own.list)} data-testid="periods">
      {periods.map((period) => (
        <li key={period.id} className={cls(own.row)}>
          <span className={cls(own.name)}>{period.label}</span>
          <span className={cls(own.meta)}>
            {period.legal_entity_name} · {regimeLabel(period.regime_key, regimes.data)} · {shortDate(period.start_date)} to{" "}
            {shortDate(period.end_date)} · {period.status}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** Hand a write's promise to the screen, which shows its outcome in one place.
 *
 * `again` is for a write that can reach a closed period (ADR-0094): the same request, sent once
 * more with an Admin's reason. Without it a refusal for `period_closed` can only be read, never
 * answered, and the Admin is told to do something the screen gives no way to do. */
export type Report = (
  result: Promise<unknown>,
  said: string,
  again?: (postCloseReason: string) => Promise<unknown>,
) => Promise<void>;

export function EntityForm({ onSubmit }: { onSubmit: Report }) {
  const create = useCreateEntity();
  const market = useMarketDefaults();
  const [name, setName] = useState("");
  // **The starting jurisdiction is the deployment's one market default** (`GET /api/v1/config`),
  // not a literal: a Californian workspace was offered "United Kingdom" first. `null` means the
  // person has not chosen, so the field follows the default once it has loaded.
  const [chosen, setJurisdiction] = useState<JurisdictionValue | null>(null);
  const jurisdiction = chosen ?? ((market?.jurisdiction ?? "OTHER") as JurisdictionValue);
  const [isParent, setIsParent] = useState(false);
  // **Asked, not assumed.** F11's defect was a Californian subsidiary with a March year end that
  // could not be looked at at all, because the window was a constant. 31 December is the common
  // case and is pre-filled; it is a visible field so somebody with a different year end changes it
  // here rather than discovering later that every period is wrong.
  const [yearEndMonth, setYearEndMonth] = useState(12);
  const [yearEndDay, setYearEndDay] = useState(31);

  return (
    <form
      className={cls(own.form)}
      data-testid="entity-form"
      onSubmit={(event) => {
        event.preventDefault();
        void onSubmit(
          create
            .mutateAsync({
              legal_name: name,
              jurisdiction,
              is_parent: isParent,
              fiscal_year_end_month: yearEndMonth,
              fiscal_year_end_day: yearEndDay,
            })
            .then(() => {
              setName("");
              setIsParent(false);
            }),
          `${name} added.`,
        );
      }}
    >
      <label className={cls(own.field)}>
        <span className={cls(own.label)}>Registered name</span>
        <input
          className={cls(own.input)}
          value={name}
          required
          onChange={(event) => setName(event.target.value)}
          placeholder="Legal name, as registered"
        />
      </label>
      <label className={cls(own.field)}>
        <span className={cls(own.label)}>Where it is incorporated</span>
        <select
          className={cls(own.input)}
          value={jurisdiction}
          onChange={(event) => setJurisdiction(event.target.value as JurisdictionValue)}
        >
          {JURISDICTIONS.map((j) => (
            <option key={j.value} value={j.value}>
              {j.label}
            </option>
          ))}
        </select>
      </label>
      <label className={cls(own.field)}>
        <span className={cls(own.label)}>Accounting year ends</span>
        <span className={cls(own.pair)}>
          <input
            type="number"
            min={1}
            max={31}
            className={cls(own.input)}
            value={yearEndDay}
            required
            aria-label="Year end day"
            onChange={(event) => setYearEndDay(Number(event.target.value))}
          />
          <select
            className={cls(own.input)}
            value={yearEndMonth}
            aria-label="Year end month"
            onChange={(event) => setYearEndMonth(Number(event.target.value))}
          >
            {MONTHS.map((month, index) => (
              <option key={month} value={index + 1}>
                {month}
              </option>
            ))}
          </select>
        </span>
        <span className={cls(own.hint)}>
          The reporting year follows this. 29 February is refused — a year end that happens in one
          year out of four produces a period that is silently wrong in the other three.
        </span>
      </label>
      <label className={cls(own.check)}>
        <input
          type="checkbox"
          checked={isParent}
          onChange={(event) => setIsParent(event.target.checked)}
        />
        <span>
          This is the group parent
          <span className={cls(own.hint)}>
            The company a group report is filed by. A group has one.
          </span>
        </span>
      </label>
      {/* **Cancel, on a form that is always open.** `cancel` has meaning on a screen with a form
          (EXP-29), and on an always-open one it means *put the fields back* rather than *close the
          panel*. It appears only once there is something to put back, so it is never a control
          that does nothing — and it restores every field, because a name cleared while a
          jurisdiction and a year end stay behind is how the next company inherits a year end
          nobody chose. */}
      <span className={cls(own.buttons)}>
        <button type="submit" className={cls(own.submit)} disabled={create.isPending}>
          {create.isPending ? "Adding…" : "Add company"}
        </button>
        {name || isParent || chosen !== null || yearEndMonth !== 12 || yearEndDay !== 31 ? (
          <button
            type="button"
            className={cls(own.cancel)}
            data-testid="cancel-entity"
            onClick={() => {
              setName("");
              setJurisdiction(null);
              setIsParent(false);
              setYearEndMonth(12);
              setYearEndDay(31);
            }}
          >
            Cancel
          </button>
        ) : null}
      </span>
    </form>
  );
}

export function FacilityForm({
  entities,
  onSubmit,
}: {
  entities: ReadonlyArray<{ id: string; name: string }>;
  onSubmit: Report;
}) {
  const create = useCreateFacility();
  const market = useMarketDefaults();
  const [entityId, setEntityId] = useState("");
  const [name, setName] = useState("");
  const [chosen, setJurisdiction] = useState<JurisdictionValue | null>(null);
  const jurisdiction = chosen ?? ((market?.jurisdiction ?? "OTHER") as JurisdictionValue);
  const [siteType, setSiteType] = useState<FacilityTypeValue>("other");
  //: **`unknown` is the default, and that is the decision rather than laziness** (ADR-0034). An
  //: answer invented to get past a required field is worse than an unanswered one: an unanswered
  //: site holds every filing it contributes to and says why, which is the behaviour the whole
  //: thing exists for. A preselected "owned" would be a guess wearing a default's clothes.
  const [leaseType, setLeaseType] = useState("unknown");
  const [operates, setOperates] = useState<boolean | null>(null);
  //: **Asked here, because it is not optional in effect** (US walkthrough, B2). A site with no grid
  //: region cannot have electricity priced, and the add form never asked - the first electricity
  //: entry failed later, on another screen. `""` is "not answered yet" (the form will not submit
  //: where a choice exists), `GRID_UNKNOWN` is a stated "I do not know", sent as no region.
  const [gridRegion, setGridRegion] = useState("");

  if (entities.length === 0) {
    return <p className={cls(own.note)}>Add a company first — a site belongs to one.</p>;
  }

  return (
    <form
      className={cls(own.form)}
      data-testid="facility-form"
      onSubmit={(event) => {
        event.preventDefault();
        void onSubmit(
          create
            .mutateAsync({
              legal_entity_id: entityId || entities[0]!.id,
              name,
              jurisdiction,
              facility_type: siteType,
              // The country the site is in. Derived from the jurisdiction rather than asked
              // twice: "US-CA" is a US site, and two fields that can disagree is one too many.
              country: countryOf(jurisdiction),
              ...(gridRegion !== "" && gridRegion !== GRID_UNKNOWN
                ? { grid_region: gridRegion }
                : {}),
              lease_type: leaseType as "owned",
              // Only sent when it is a question this lease type turns on. An owned site is
              // operated by whoever owns it as far as this question goes, and the database
              // refuses "owned and we do not operate it" — a landlord relationship is a record
              // this product does not model.
              operates: leaseType === "owned" ? true : operates,
            })
            .then(() => {
              setName("");
              setGridRegion("");
            }),
          `${name} added.`,
        );
      }}
    >
      <label className={cls(own.field)}>
        <span className={cls(own.label)}>Company</span>
        <select
          className={cls(own.input)}
          value={entityId || entities[0]!.id}
          onChange={(event) => setEntityId(event.target.value)}
        >
          {entities.map((entity) => (
            <option key={entity.id} value={entity.id}>
              {entity.name}
            </option>
          ))}
        </select>
      </label>
      <label className={cls(own.field)}>
        <span className={cls(own.label)}>Site name</span>
        <input
          className={cls(own.input)}
          value={name}
          required
          onChange={(event) => setName(event.target.value)}
          placeholder="Site name"
        />
      </label>
      <label className={cls(own.field)}>
        <span className={cls(own.label)}>What happens there</span>
        <select
          className={cls(own.input)}
          value={siteType}
          onChange={(event) => setSiteType(event.target.value as FacilityTypeValue)}
        >
          {SITE_TYPES.map((type) => (
            <option key={type} value={type}>
              {type.replace(/_/g, " ")}
            </option>
          ))}
        </select>
      </label>
      <label className={cls(own.field)}>
        <span className={cls(own.label)}>Country</span>
        <select
          className={cls(own.input)}
          value={jurisdiction}
          onChange={(event) => {
            setJurisdiction(event.target.value as JurisdictionValue);
            // A region belongs to a jurisdiction's factor set: CAMX means nothing for a UK site.
            setGridRegion("");
          }}
        >
          {JURISDICTIONS.map((j) => (
            <option key={j.value} value={j.value}>
              {j.label}
            </option>
          ))}
        </select>
      </label>
      {jurisdiction === "OTHER" ? null : (
        <GridRegionField
          mode="add"
          idBase="add-site"
          jurisdiction={jurisdiction}
          value={gridRegion}
          stored={null}
          onChange={setGridRegion}
        />
      )}

      {/* **ADR-0034, and the question EXP-08 asks for.**
          Not required, and "Not answered" is preselected. An answer invented to get past a
          required field is worse than an unanswered one: an unanswered site is held out of a
          filing's stated total with a finding naming it, and a guess silently lands in one. */}
      <label className={cls(own.field)}>
        <span className={cls(own.label)}>What is the company&rsquo;s relationship to it?</span>
        <select
          className={cls(own.input)}
          value={leaseType}
          data-testid="lease-type"
          onChange={(event) => {
            setLeaseType(event.target.value);
            // The second question only exists for some answers, so a stale `operates` from a
            // previous choice must not travel with a new one.
            setOperates(null);
          }}
        >
          {["unknown", "owned", "finance_lease", "operating_lease", "occupied_under_licence"].map(
            (value) => (
              <option key={value} value={value}>
                {LEASE_LABEL[value]}
              </option>
            ),
          )}
        </select>
      </label>
      <p className={cls(own.note)} data-testid="lease-help">
        {LEASE_HELP[leaseType]}
      </p>

      {/* **Only where it decides something.** Under operational control, whether the company
          operates a leased site is the fact that settles it; for an owned site it is not a
          question, and the database refuses "owned and we do not operate it" because that is a
          landlord relationship this product does not model. */}
      {leaseType !== "owned" && leaseType !== "unknown" ? (
        <label className={cls(own.field)}>
          <span className={cls(own.label)}>Does the company operate it?</span>
          <select
            className={cls(own.input)}
            value={operates === null ? "" : operates ? "yes" : "no"}
            data-testid="operates"
            onChange={(event) =>
              setOperates(event.target.value === "" ? null : event.target.value === "yes")
            }
          >
            <option value="">Not answered</option>
            <option value="yes">Yes — we run what happens there</option>
            <option value="no">No — somebody else does</option>
          </select>
        </label>
      ) : null}

      <span className={cls(own.buttons)}>
        <button type="submit" className={cls(own.submit)} disabled={create.isPending}>
          {create.isPending ? "Adding…" : "Add site"}
        </button>
        {name || leaseType !== "unknown" || operates !== null ? (
          <button
            type="button"
            className={cls(own.cancel)}
            data-testid="cancel-facility"
            onClick={() => {
              setName("");
              setGridRegion("");
              setLeaseType("unknown");
              setOperates(null);
            }}
          >
            Cancel
          </button>
        ) : null}
      </span>
    </form>
  );
}

/** A site's lease treatment - **ADR-0068 option C, QA F-032**.
 *
 * `create_facility` leaves it `unknown` on purpose, and `regimes.validation` then holds every
 * filing the site contributes to *"until somebody answers"* - while no route let anyone answer. A
 * guard whose remedy nothing could perform.
 *
 * **Four rules, and the third is the one that is easy to leave out.**
 *
 * - **First answer and later revisions are separate acts.** The existing first-answer form stays
 *   here; the history popup uses the dated correction service when that server enables it.
 * - **Both facts, and a basis.** `lease_type` *and* `operates` - operational control asks whether
 *   the company operates the site, financial control asks whether it is a finance lease - and who
 *   confirmed it, because the GHG Protocol says the classification is the accountant's to give.
 *   An owned site is not asked whether it operates it: the database refuses "owned and not
 *   operated".
 * - **The consequence is said before the button, not after.** The boundary now applies to filed
 *   totals (F-043), so this answer **moves a stated total**, in whichever direction each filing's
 *   own consolidation approach decides. A person who finds that out from the report afterwards has
 *   been surprised by their own act.
 * - **Nothing already issued is changed**, and the confirmation says so; an issued filing will
 *   stop matching today's figures when re-checked, which is the ordinary signal and not damage.
 */
export function LeaseTreatment({ site, onSubmit }: { site: Facility; onSubmit: Report }) {
  const answer = useAnswerLease();
  const [open, setOpen] = useState(false);
  const [leaseType, setLeaseType] = useState("");
  const [operates, setOperates] = useState<boolean | null>(null);
  const [basis, setBasis] = useState("");
  /** The count the server returned with the answer. Held here because the list re-reads and this
   * component survives the re-render, so the sentence is still on screen when it does. */
  const [issued, setIssued] = useState<number | null>(null);

  // The server owns the cutover. An UNKNOWN baseline also needs a dated first correction once
  // the legacy writer is fenced; inferring readiness from the old generated shape would strand it.
  if ("lease_corrections_available" in site && site.lease_corrections_available === true) {
    return <LeaseCorrections facilityId={site.id} facilityName={site.name} />;
  }

  // `issued !== null` is "the server accepted it": the list has not re-read yet, and for that
  // moment the row still says unknown. Offering the control again there is how a double click
  // becomes a 409 on an act that succeeded.
  if (site.lease_type !== "unknown" || issued !== null) {
    return (
      <div data-testid={`lease-answered-${site.id}`}>
      <p className={cls(own.note)}>
        {site.operates === null
          ? "Lease treatment stated."
          : site.operates
            ? "Lease treatment stated; the company operates this site."
            : "Lease treatment stated; somebody else operates this site."}{" "}
        If it was wrong, or it changed on a date, record that with the date and a reason. The
        previous answer stays in the history, and you see the filings it moves before you save.
        {issued !== null && issued > 0 ? (
          <span data-testid="lease-issued-note">
            {" "}
            {issued} issued filing{issued === 1 ? "" : "s"} in this workspace {issued === 1 ? "was" : "were"}{" "}
            not altered; any whose boundary reaches this site will stop matching today&rsquo;s
            figures when re-checked.
          </span>
        ) : null}
      </p>
      <LeaseCorrections facilityId={site.id} facilityName={site.name} />
      </div>
    );
  }

  const ready =
    leaseType !== "" && basis.trim() !== "" && (leaseType === "owned" || operates !== null);

  function reset() {
    setOpen(false);
    setLeaseType("");
    setOperates(null);
    setBasis("");
  }

  if (!open) {
    return (
      <button
        type="button"
        className={cls(own.inlineButton)}
        data-testid={`answer-lease-${site.id}`}
        onClick={() => setOpen(true)}
      >
        State the lease treatment
      </button>
    );
  }

  return (
    <form
      className={cls(own.form)}
      data-testid="lease-answer-form"
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready) return;
        void onSubmit(
          answer
            .mutateAsync({
              facilityId: site.id,
              lease_type: leaseType as "owned",
              operates: leaseType === "owned" ? true : operates,
              basis: basis.trim(),
            })
            .then((result) => {
              setIssued(result.issued_filings);
              reset();
            }),
          `${site.name}: lease treatment recorded. Filed totals now count this site, or leave it out, according to each filing's own consolidation approach.`,
        );
      }}
    >
      <label className={cls(own.field)}>
        <span className={cls(own.label)}>What is the company&rsquo;s relationship to {site.name}?</span>
        <select
          className={cls(own.input)}
          value={leaseType}
          data-testid="lease-answer-type"
          onChange={(event) => {
            setLeaseType(event.target.value);
            // The second question only exists for some answers; a stale one must not travel.
            setOperates(null);
          }}
        >
          <option value="">Choose&hellip;</option>
          {["owned", "finance_lease", "operating_lease", "occupied_under_licence"].map((value) => (
            <option key={value} value={value}>
              {LEASE_LABEL[value]}
            </option>
          ))}
        </select>
        <span className={cls(own.hint)}>
          {leaseType === "" ? "Ask the company's accountant if you are not sure." : LEASE_HELP[leaseType]}
        </span>
      </label>

      {leaseType !== "" && leaseType !== "owned" ? (
        <label className={cls(own.field)}>
          <span className={cls(own.label)}>Does the company operate it?</span>
          <select
            className={cls(own.input)}
            value={operates === null ? "" : operates ? "yes" : "no"}
            data-testid="lease-answer-operates"
            onChange={(event) =>
              setOperates(event.target.value === "" ? null : event.target.value === "yes")
            }
          >
            <option value="">Choose&hellip;</option>
            <option value="yes">Yes &mdash; we run what happens there</option>
            <option value="no">No &mdash; somebody else does</option>
          </select>
        </label>
      ) : null}

      <label className={cls(own.field)}>
        <span className={cls(own.label)}>Who confirmed this, and on what basis?</span>
        <textarea
          className={cls(own.input)}
          value={basis}
          required
          maxLength={1000}
          rows={2}
          data-testid="lease-answer-basis"
          placeholder="e.g. Confirmed by the finance director from the FY2025 lease schedule"
          onChange={(event) => setBasis(event.target.value)}
        />
        <span className={cls(own.hint)}>
          Kept as a note on the site. It is not written to the audit chain, which cannot be erased.
        </span>
      </label>

      {/* **Before the button, and in the form rather than in the confirmation.** */}
      <p className={cls(own.note)} data-testid="lease-moves-total">
        <strong>This changes what the filed total contains.</strong> Today this site&rsquo;s
        figures are held out of every filing&rsquo;s stated total. Once answered they are counted,
        or kept out, under each filing&rsquo;s own consolidation approach &mdash; so a stated total
        will move. Nothing is recalculated and no receipt changes. Filings already issued are not
        altered; they will stop matching today&rsquo;s figures when re-checked.
      </p>

      <span className={cls(own.buttons)}>
        <button type="submit" className={cls(own.submit)} disabled={answer.isPending || !ready}>
          {answer.isPending ? "Saving..." : "Confirm this answer"}
        </button>
        <button type="button" className={cls(own.cancel)} data-testid="cancel-lease-answer" onClick={reset}>
          Cancel
        </button>
      </span>
    </form>
  );
}

export function SitePanel({
  siteId,
  siteName,
  onSubmit,
}: {
  siteId: string;
  siteName: string;
  onSubmit: Report;
}) {
  const meters = useMeters(siteId);
  const createMeter = useCreateMeter();
  const retire = useRetireFacility();
  const [kind, setKind] = useState<MeterKindValue>("electricity");
  const [unit, setUnit] = useState("kWh");
  const [label, setLabel] = useState("");
  const [closing, setClosing] = useState("");
  /** Which meter's retirement form is open — **one at a time, and the id is the state**.
   *
   * A `boolean` per row would need a map; a shared open flag would put one date control under
   * whichever row was clicked last and leave the others looking retired. The date lives in the
   * form component, so switching rows cannot carry a date typed for a different supply point. */
  const [retiring, setRetiring] = useState<string | null>(null);

  return (
    <div className={cls(own.sub)} data-testid="site-panel">
      {meters.error ? (
        <ReadFailed
          of="This site's meters"
          error={meters.error}
          onRetry={() => void meters.refetch()}
        />
      ) : meters.isPending ? (
        <p className={cls(own.note)}>Reading meters…</p>
      ) : meters.data?.length ? (
        <ul className={cls(own.meters)}>
          {meters.data.map((meter) => (
            <li key={meter.id} className={cls(own.meter)}>
              {meter.label ?? meter.kind.replace(/_/g, " ")}
              <span className={cls(own.meta)}>
                {meter.kind.replace(/_/g, " ")} · {unitLabel(meter.unit)}
                {/* A retired meter is **listed**, because it is why last year's readings exist —
                    and the date is in **words**: `30-design-principles.md` rule 7, which this line
                    was breaking by printing `2026-03-31` straight from the wire. */}
                {meter.operational_to ? (
                  <span className={cls(own.retired)}>
                    {" · "}retired {shortDate(meter.operational_to)}
                  </span>
                ) : null}
              </span>
              {/* **CTX-03's acceptance clause: the control.** `POST /meters/{id}/retire` has
                  existed, tested and documented, with no screen calling it — `useIssueArtifact`'s
                  shape, which this project has now shipped three times. Before this the only way
                  to record one supply point coming out was to close the whole site, which retires
                  every supply at it.

                  Offered for a **retired** meter too, because retiring to a different date is a
                  real change the service writes (a replacement that arrived later than planned),
                  and a control that disappeared would leave a wrong date correctable only by an
                  engineer. The label says which act it is. */}
              <button
                type="button"
                className={cls(own.inlineButton)}
                data-testid={`retire-meter-${meter.id}`}
                aria-expanded={retiring === meter.id}
                onClick={() => setRetiring(retiring === meter.id ? null : meter.id)}
              >
                {retiring === meter.id
                  ? "Hide"
                  : meter.operational_to
                    ? "Change when it came out"
                    : "Retire this meter"}
              </button>
              {retiring === meter.id ? (
                <MeterRetirement
                  meter={meter}
                  siteId={siteId}
                  onSubmit={onSubmit}
                  onDone={() => setRetiring(null)}
                />
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className={cls(own.note)}>No meters at this site yet.</p>
      )}

      {/* **Where the consequence is read** (EXP-06's navigable half). A retirement's whole effect
          is on the census: the months after it stop being judged against this site's streams. The
          meter register had no edge to any other register, so an Admin who had just retired a
          supply point had no way to see the product agree. */}
      <p className={cls(own.note)}>
        <Link to="/sources" search={keepSearch} className={cls(own.crossLink)}>
          Source census
        </Link>{" "}
        — what is still expected from this site, and what each stream has produced.
      </p>

      <form
        className={cls(own.form)}
        data-testid="meter-form"
        onSubmit={(event) => {
          event.preventDefault();
          void onSubmit(
            createMeter
              .mutateAsync({ facility_id: siteId, kind, unit, label: label || null })
              .then(() => setLabel("")),
            `Meter added to ${siteName}.`,
          );
        }}
      >
        <label className={cls(own.field)}>
          <span className={cls(own.label)}>What it meters</span>
          <select
            className={cls(own.input)}
            value={kind}
            onChange={(event) => setKind(event.target.value as MeterKindValue)}
          >
            {METER_KINDS.map((k) => (
              <option key={k} value={k}>
                {k.replace(/_/g, " ")}
              </option>
            ))}
          </select>
        </label>
        <label className={cls(own.field)}>
          <span className={cls(own.label)}>Unit it reads in</span>
          <input
            className={cls(own.input)}
            value={unit}
            required
            onChange={(event) => setUnit(event.target.value)}
          />
        </label>
        <label className={cls(own.field)}>
          <span className={cls(own.label)}>Label (optional)</span>
          <input
            className={cls(own.input)}
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            placeholder="Incoming supply"
          />
        </label>
        <span className={cls(own.buttons)}>
          <button type="submit" className={cls(own.submit)} disabled={createMeter.isPending}>
            Add meter
          </button>
          {label || unit !== "kWh" || kind !== "electricity" ? (
            <button
              type="button"
              className={cls(own.cancel)}
              data-testid="cancel-meter"
              onClick={() => {
                setKind("electricity");
                setUnit("kWh");
                setLabel("");
              }}
            >
              Cancel
            </button>
          ) : null}
        </span>
      </form>

      {/* **Retire, never delete**, and the control says what it will do before it is pressed. */}
      <form
        className={cls(own.form)}
        data-testid="retire-form"
        onSubmit={(event) => {
          event.preventDefault();
          void onSubmit(
            retire.mutateAsync({ facilityId: siteId, effective_from: closing }),
            `${siteName} retired. Its figures stay in every report that already used them.`,
            (postCloseReason) =>
              retire.mutateAsync({ facilityId: siteId, effective_from: closing, postCloseReason }),
          );
        }}
      >
        <label className={cls(own.field)}>
          <span className={cls(own.label)}>Last day open</span>
          <input
            type="date"
            className={cls(own.input)}
            value={closing}
            required
            onChange={(event) => setClosing(event.target.value)}
          />
        </label>
        <span className={cls(own.buttons)}>
          <button type="submit" className={cls(own.retire)} disabled={retire.isPending}>
            Retire this site
          </button>
          {closing ? (
            <button
              type="button"
              className={cls(own.cancel)}
              data-testid="cancel-retire"
              onClick={() => setClosing("")}
            >
              Cancel
            </button>
          ) : null}
        </span>
        <p className={cls(own.hint)}>
          Nothing is deleted. Nothing is expected from the day after this date, its live meters retire
          on the same date, and every figure that cites it stays in the reports that already used them.
        </p>
      </form>
    </div>
  );
}

/** One supply point coming out — **CTX-03**, and the narrower act of the two on this panel.
 *
 * **Its own component because the date is its own state.** Held in `SitePanel` it would be one
 * string shared by every row, so a date typed against the gas meter would be sitting in the form
 * the moment somebody opened the electricity meter's — a retirement is the one act here where the
 * wrong row is unrecoverable without an engineer.
 *
 * **What the hint says is the part worth getting right**, because the consequence is invisible from
 * this screen. Nothing is deleted, every reading stays and every filed figure is unchanged (rule
 * 3); what changes is that the census stops judging the months afterwards — a removed supply point
 * used to go on producing a missing month for ever, which is a gap accusing the customer of not
 * sending a bill that cannot exist.
 *
 * **The off-by-one is stated rather than left to be inferred.** `MeterRetirementIn` takes the
 * **last day** the meter was expected to produce data, so a meter replaced on 1 April is retired on
 * 31 March and the March bill is still expected. A reader who assumes the other convention loses a
 * month of data or invents a phantom one, and the server cannot tell which they meant.
 */
function MeterRetirement({
  meter,
  siteId,
  onSubmit,
  onDone,
}: {
  meter: Meter;
  siteId: string;
  onSubmit: Report;
  onDone: () => void;
}) {
  const retire = useRetireMeter();
  const [when, setWhen] = useState(meter.operational_to ?? "");
  const name = meter.label ?? meter.kind.replace(/_/g, " ");

  return (
    <form
      className={cls(own.form)}
      data-testid="meter-retire-form"
      onSubmit={(event) => {
        event.preventDefault();
        void onSubmit(
          retire
            .mutateAsync({ meterId: meter.id, facilityId: siteId, effective_from: when })
            // **Closed only on success.** `onSubmit` reports a refusal into the screen's banner,
            // and a form that closed either way would take the date the server refused with it —
            // leaving the sentence on screen and nothing to correct.
            .then(() => onDone()),
          `${name} stops being expected after ${shortDate(when)}. Its readings stay, and every figure that cites it is unchanged.`,
          (postCloseReason) =>
            retire
              .mutateAsync({
                meterId: meter.id,
                facilityId: siteId,
                effective_from: when,
                postCloseReason,
              })
              .then(() => onDone()),
        );
      }}
    >
      <label className={cls(own.field)}>
        <span className={cls(own.label)}>Last day it was expected to produce data</span>
        <input
          type="date"
          className={cls(own.input)}
          value={when}
          required
          aria-label={`Last day ${name} was expected to produce data`}
          onChange={(event) => setWhen(event.target.value)}
        />
        <span className={cls(own.hint)}>
          A meter replaced on 1 April comes out on 31 March — the March bill is still expected. A
          date before it was installed is refused, because that describes a life with no days in it.
        </span>
      </label>
      <span className={cls(own.buttons)}>
        <button type="submit" className={cls(own.retire)} disabled={retire.isPending}>
          {retire.isPending ? "Recording…" : "Retire this meter"}
        </button>
        <button
          type="button"
          className={cls(own.cancel)}
          data-testid="cancel-meter-retire"
          onClick={() => {
            setWhen(meter.operational_to ?? "");
            onDone();
          }}
        >
          Cancel
        </button>
      </span>
      <p className={cls(own.hint)}>
        Nothing is deleted and the meter stays listed at this site — it is why last year&rsquo;s
        readings exist. The expected bills stops expecting data from it after that date; the
        stream&rsquo;s own dates and the site&rsquo;s are untouched, so a reader is told which of
        the three ended it.
      </p>
    </form>
  );
}

export function PeriodForm({
  entities,
  onSubmit,
}: {
  entities: ReadonlyArray<{ id: string; name: string }>;
  onSubmit: Report;
}) {
  const create = useCreatePeriod();
  const [entityId, setEntityId] = useState("");
  // **The deployment's default regime until somebody picks one**, and the first loaded regime if
  // the default is not among them. `null` is "not chosen"; nothing is typed in here (ADR-0199).
  const regimes = useRegimes();
  const market = useMarketDefaults();
  const [chosenRegime, setRegime] = useState<string | null>(null);
  const available = regimes.data ?? [];
  const regime =
    chosenRegime ??
    (available.some((entry) => entry.key === market?.regime_key)
      ? market?.regime_key
      : available[0]?.key) ??
    "";
  const [label, setLabel] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  // **Nothing selected until somebody selects it.** See the module docstring.
  // The empty string is not a member of the union, which is the point: it is the state
  // before a choice, and `required` is what stops it being submitted.
  const [approach, setApproach] = useState<ApproachValue | "">("");

  if (entities.length === 0) {
    return <p className={cls(own.note)}>Add a company first — a reporting period belongs to one.</p>;
  }

  return (
    <form
      className={cls(own.form)}
      data-testid="period-form"
      onSubmit={(event) => {
        event.preventDefault();
        if (!approach) return;
        void onSubmit(
          create
            .mutateAsync({
              legal_entity_id: entityId || entities[0]!.id,
              regime_key: regime,
              label,
              start_date: start,
              end_date: end,
              // No cast: `if (!approach) return` above narrows it, which is the honest version —
              // a cast here would still compile on the day somebody removes that guard.
              boundary_approach: approach,
            })
            .then(() => setLabel("")),
          `${label} added.`,
        );
      }}
    >
      <label className={cls(own.field)}>
        <span className={cls(own.label)}>Company</span>
        <select
          className={cls(own.input)}
          value={entityId || entities[0]!.id}
          onChange={(event) => setEntityId(event.target.value)}
        >
          {entities.map((entity) => (
            <option key={entity.id} value={entity.id}>
              {entity.name}
            </option>
          ))}
        </select>
      </label>
      {/* **A failed read is not "no regime is loaded".** The select below would be empty either way,
          and a filing window cannot be declared without a regime. */}
      {regimes.error ? (
        <div className={cls(own.field)} data-testid="regimes-failed">
          <ReadFailed
            of="The list of regimes"
            error={regimes.error}
            onRetry={() => void regimes.refetch()}
          />
        </div>
      ) : (
        <label className={cls(own.field)}>
          <span className={cls(own.label)}>Regime</span>
          <select
            className={cls(own.input)}
            value={regime}
            onChange={(event) => setRegime(event.target.value)}
          >
            {available.map((entry) => (
              <option key={entry.key} value={entry.key}>
                {entry.label}
              </option>
            ))}
          </select>
        </label>
      )}
      <label className={cls(own.field)}>
        <span className={cls(own.label)}>Label</span>
        <input
          className={cls(own.input)}
          value={label}
          required
          onChange={(event) => setLabel(event.target.value)}
          placeholder="FY2026"
        />
      </label>
      <label className={cls(own.field)}>
        <span className={cls(own.label)}>From</span>
        <input
          type="date"
          className={cls(own.input)}
          value={start}
          required
          onChange={(event) => setStart(event.target.value)}
        />
      </label>
      <label className={cls(own.field)}>
        <span className={cls(own.label)}>To</span>
        <input
          type="date"
          className={cls(own.input)}
          value={end}
          required
          onChange={(event) => setEnd(event.target.value)}
        />
      </label>
      <label className={cls(own.field)}>
        <span className={cls(own.label)}>Consolidation approach</span>
        <select
          className={cls(own.input)}
          value={approach}
          required
          onChange={(event) => setApproach(event.target.value as ApproachValue | "")}
          data-testid="boundary-approach"
        >
          <option value="">Choose one…</option>
          {APPROACHES.map((a) => (
            <option key={a.value} value={a.value}>
              {a.label}
            </option>
          ))}
        </select>
        <span className={cls(own.hint)}>
          {APPROACHES.find((a) => a.value === approach)?.means ??
            "The GHG Protocol requires one of three, and the choice decides whether a joint venture's emissions are in this inventory at all. There is no default."}
        </span>
      </label>
      <span className={cls(own.buttons)}>
        <button type="submit" className={cls(own.submit)} disabled={create.isPending}>
          {create.isPending ? "Adding…" : "Add reporting period"}
        </button>
        {approach ? (
          <button
            type="button"
            className={cls(own.cancel)}
            data-testid="cancel-period"
            onClick={() => setApproach("")}
          >
            Cancel
          </button>
        ) : null}
      </span>
    </form>
  );
}
